import { useState, useEffect } from 'react';
import * as React from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { config } from '../config';
import { getAuthToken } from '../utils/authToken';
import { fetchWithRetry } from '../utils/fetchWithRetry';

export default function VaseDataPage() {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [successType, setSuccessType] = useState<'export' | 'deletion' | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const handleLogout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: '/'
    });
  };

  // Get user email on component mount
  React.useEffect(() => {
    if (accounts[0]?.username) {
      setUserEmail(accounts[0].username);
    }
  }, [accounts]);

  const handleExportClick = () => {
    setErrorMessage('');
    setShowExportModal(true);
  };

  const handleDeletionClick = () => {
    setErrorMessage('');
    setShowDeletionModal(true);
  };

  const handleExportConfirm = async () => {
    setIsProcessing(true);
    setErrorMessage('');

    try {
      const token = await getAuthToken(instance);
      if (!token) {
        setErrorMessage('❌ Chyba autentifikácie');
        setIsProcessing(false);
        return;
      }

      const response = await fetchWithRetry(`${config.apiUrl}/requestDataExport`, {
        method: 'POST',
        headers: {
          'X-Auth-Token': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setShowExportModal(false);
        setSuccessType('export');
        setShowSuccessScreen(true);
      } else {
        // Handle business errors
        if (response.status === 429) {
          // Extract next available time from the response if available
          const nextAvailable = data.nextAvailableAt ? new Date(data.nextAvailableAt).toLocaleString('sk-SK') : '';
          const message = nextAvailable
            ? `⏱️ Dnes si už o stiahnutie svojich dát požiadal. Ďalší export bude možný ${nextAvailable}.`
            : `⏱️ ${data.message || 'Dnes si už o stiahnutie svojich dát požiadal. Skús to neskôr.'}`;
          setErrorMessage(message);
        } else {
          setErrorMessage(`❌ ${data.message || 'Nepodarilo sa vytvoriť export. Skúste to prosím neskôr.'}`);
        }
      }
    } catch (error) {
      console.error('Export request failed:', error);
      setErrorMessage('❌ Nepodarilo sa spojiť so serverom. Skúste to prosím neskôr.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeletionConfirm = async () => {
    setIsProcessing(true);
    setErrorMessage('');

    try {
      const token = await getAuthToken(instance);
      if (!token) {
        setErrorMessage('❌ Chyba autentifikácie');
        setIsProcessing(false);
        return;
      }

      const response = await fetchWithRetry(`${config.apiUrl}/requestDataDeletion`, {
        method: 'POST',
        headers: {
          'X-Auth-Token': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setShowDeletionModal(false);
        setSuccessType('deletion');
        setShowSuccessScreen(true);
      } else {
        // Handle business errors
        if (response.status === 409) {
          setErrorMessage(`⚠️ ${data.message || 'Žiadosť o vymazanie už existuje. Skontrolujte svoj e-mail.'}`);
        } else if (response.status === 400 && data.error === 'Account already deleted') {
          setErrorMessage(`⚠️ ${data.message || 'Tento účet už bol vymazaný.'}`);
        } else {
          setErrorMessage(`❌ ${data.message || 'Nepodarilo sa vytvoriť žiadosť. Skúste to prosím neskôr.'}`);
        }
      }
    } catch (error) {
      console.error('Deletion request failed:', error);
      setErrorMessage('❌ Nepodarilo sa spojiť so serverom. Skúste to prosím neskôr.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px 20px 100px 20px' }}>
      {/* Back link */}
      <button
        onClick={() => navigate('/profile')}
        style={{
          background: 'none',
          border: 'none',
          color: '#667eea',
          fontSize: '14px',
          fontWeight: 500,
          cursor: 'pointer',
          padding: '0 0 16px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Späť na profil
      </button>

      <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
        Tvoje dáta
      </h1>
      <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '32px' }}>
        Čo môžeš urobiť so svojimi dátami v SmartBločku
      </p>

      {/* Error message banner */}
      {errorMessage && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          fontSize: '14px',
          color: '#991b1b'
        }}>
          {errorMessage}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Export card */}
        <DataActionCard
          icon="📦"
          title="Stiahnuť všetky svoje dáta"
          description="Stiahni si svoje dáta v strojovo čitateľnom formáte. (právo na prenosnosť údajov)"
          buttonLabel="Stiahnuť"
          buttonColor="#667eea"
          onButtonClick={handleExportClick}
        />

        {/* Deletion card */}
        <DataActionCard
          icon="🗑️"
          title="Vymazať všetky svoje dáta"
          description="Vymaž natrvalo svoj účet a všetky svoje dáta. (právo na vymazanie)"
          warning="Nenávratné"
          buttonLabel="Vymazať účet"
          buttonColor="#dc2626"
          onButtonClick={handleDeletionClick}
        />
      </div>

      {/* Export modal */}
      {showExportModal && (
        <Modal
          title="Stiahnuť všetky svoje dáta"
          onClose={() => setShowExportModal(false)}
        >
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '15px', color: '#374151', lineHeight: '1.6', marginBottom: '16px' }}>
              Vytvoríme kompletný export všetkých tvojich dát v SmartBloček.
            </p>

            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px', fontWeight: 500 }}>
              Čo bude v exporte:
            </div>
            <ul style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.8', margin: '0 0 16px 0', paddingLeft: '20px' }}>
              <li>Profil (JSON)</li>
              <li>Všetky bločky (CSV)</li>
              <li>Položky a kategórie (CSV)</li>
              <li>Rozúčtovania (CSV)</li>
              <li>Uložené fotografie bločkov (ZIP)</li>
            </ul>

            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', color: '#1e40af', lineHeight: '1.6' }}>
                <strong>ℹ️ Dôležité:</strong><br />
                • Export môžeš vytvoriť raz za 24 hodín<br />
                • Odkaz na stiahnutie dostaneš e-mailom do pár hodín<br />
                • Odkaz bude platný 7 dní
              </div>
            </div>
          </div>

          {errorMessage && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px', marginBottom: '16px', fontSize: '14px', color: '#991b1b' }}>
              {errorMessage}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowExportModal(false)}
              disabled={isProcessing}
              style={{
                background: '#e5e7eb',
                color: '#374151',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.5 : 1
              }}
            >
              Zrušiť
            </button>
            <button
              onClick={handleExportConfirm}
              disabled={isProcessing}
              style={{
                background: '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.8 : 1
              }}
            >
              {isProcessing ? 'Odosielam...' : 'Vytvoriť export'}
            </button>
          </div>
        </Modal>
      )}

      {/* Deletion modal */}
      {showDeletionModal && (
        <Modal
          title="Vymazať všetky svoje dáta"
          onClose={() => setShowDeletionModal(false)}
        >
          <div style={{ marginBottom: '24px' }}>
            <div style={{ background: '#fef2f2', border: '2px solid #fecaca', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', color: '#991b1b', fontWeight: 700, marginBottom: '8px' }}>
                ⚠️ DÔLEŽITÉ: Táto akcia je nenávratná!
              </div>
              <div style={{ fontSize: '13px', color: '#7f1d1d', lineHeight: '1.6' }}>
                Po potvrdení budú natrvalo vymazané všetky tvoje dáta. Tento úkon nie je možné vrátiť späť.
              </div>
            </div>

            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px', fontWeight: 500 }}>
              Čo sa zmaže:
            </div>
            <ul style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.8', margin: '0 0 16px 0', paddingLeft: '20px' }}>
              <li>Tvoj účet (prístup a profil)</li>
              <li>Všetky tvoje bločky a položky</li>
              <li>Analýzy a odvodené výsledky</li>
              <li>Všetky GDPR exporty</li>
            </ul>

            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px', fontWeight: 500 }}>
              Čo zostane (zákonné dôvody):
            </div>
            <ul style={{ fontSize: '13px', color: '#9ca3af', lineHeight: '1.7', margin: '0 0 16px 0', paddingLeft: '20px' }}>
              <li>Záznamy o vymazaní (anonymizované, 90 dní)</li>
              <li>Zdieľané dáta o obchodníkoch a produktoch</li>
            </ul>

            <div style={{ background: '#f5f3ff', border: '1px solid #e9d5ff', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', color: '#5b21b6', lineHeight: '1.6' }}>
                <strong>💡 Pred potvrdením:</strong><br />
                Ak si požiadal o stiahnutie tvojich dát, tie si najprv stiahni a potom spusti zmazanie účtu a dát. Zmazaním tvojich dát sa zmaže aj vytvorený export určený na stiahnutie a tvoje dáta budú nenávratne zmazané.
              </div>
            </div>

            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px' }}>
              <div style={{ fontSize: '13px', color: '#1e40af', lineHeight: '1.6' }}>
                <strong>📧 Ako to funguje:</strong><br />
                1. Po kliknutí na tlačidlo Áno, vymazať účet ti pošleme na e-mail potvrdzovaciu linku<br />
                2. Zmazanie dát potvrdíš kliknutím na potvrdzovaciu linku v e-maile. Linka platí 7 dní.<br />
                3. Tvoje dáta budú natrvalo vymazané
              </div>
            </div>
          </div>

          {errorMessage && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px', marginBottom: '16px', fontSize: '14px', color: '#991b1b' }}>
              {errorMessage}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowDeletionModal(false)}
              disabled={isProcessing}
              style={{
                background: '#e5e7eb',
                color: '#374151',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.5 : 1
              }}
            >
              Zrušiť
            </button>
            <button
              onClick={handleDeletionConfirm}
              disabled={isProcessing}
              style={{
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.9 : 1
              }}
            >
              {isProcessing ? 'Vytváram žiadosť...' : 'Áno, vymazať účet'}
            </button>
          </div>
        </Modal>
      )}

      {/* Success screen */}
      {showSuccessScreen && successType && (
        <Modal
          title={successType === 'export' ? '✓ Spustili sme export tvojich dát' : '✓ Žiadosť odoslaná'}
          onClose={successType === 'deletion' ? undefined : () => {
            setShowSuccessScreen(false);
            setSuccessType(null);
          }}
          isDismissible={successType !== 'deletion'}
        >
          <div style={{ marginBottom: '24px' }}>
            {successType === 'export' ? (
              <>
                <p style={{ fontSize: '15px', color: '#374151', lineHeight: '1.6', marginBottom: '16px' }}>
                  Spustili sme export tvojich dát. Do pár hodín dostaneš email s linkou na stiahnutie.
                </p>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ fontSize: '13px', color: '#1e40af', lineHeight: '1.6' }}>
                    <strong>ℹ️ Čo ďalej:</strong><br />
                    • Priebežne si kontroluj e-mail (a prípadne aj priečinok Spam).<br />
                    • Odkaz na stiahnutie je platný 7 dní.<br />
                    • Ak si dáta nestiahneš, pred skončením platnosti ti pošleme pripomienku.
                  </div>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: '15px', color: '#374151', lineHeight: '1.6', marginBottom: '16px' }}>
                  Spustili sme proces zmazania tvojich dát. Na tvoj e-mail <strong>{userEmail}</strong> sme poslali linku na potvrdenie. Ak si praješ dáta zmazať, klikni na potvrdzujúcu linku v e-maile.
                </p>
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                  <div style={{ fontSize: '13px', color: '#991b1b', lineHeight: '1.6' }}>
                    <strong>⚠️ Upozornenie:</strong><br />
                    Po kliknutí na linku v e-maile bude tvoj účet a tvoje dáta natrvalo zmazané. Tento úkon je nenávratný.
                  </div>
                </div>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ fontSize: '13px', color: '#1e40af', lineHeight: '1.6' }}>
                    <strong>📧 Skontroluj e-mail:</strong><br />
                    • Odkaz je platný 7 dní<br />
                    • Kliknutím potvrdíš vymazanie<br />
                    • Ak si si to rozmyslel a chceš SmartBloček naďalej využívať, tak sa znova prihlás do SmartBločka a potvrď zachovanie údajov.
                  </div>
                </div>
              </>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                if (successType === 'deletion') {
                  handleLogout();
                } else {
                  setShowSuccessScreen(false);
                  setSuccessType(null);
                  navigate('/profile');
                }
              }}
              style={{
                background: '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {successType === 'deletion' ? 'Odhlásiť' : 'Späť na profil'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

interface DataActionCardProps {
  icon: string;
  title: string;
  description: string;
  warning?: string;
  buttonLabel: string;
  buttonColor: string;
  onButtonClick: () => void;
}

function DataActionCard({ icon, title, description, warning, buttonLabel, buttonColor, onButtonClick }: DataActionCardProps) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '20px',
      transition: 'box-shadow 0.2s',
      cursor: 'default'
    }}>
      <div style={{ display: 'flex', alignItems: 'start', gap: '16px', marginBottom: '16px' }}>
        <div style={{ fontSize: '32px' }}>{icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '17px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
            {title}
          </div>
          <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.5' }}>
            {description}
          </div>
          {warning && (
            <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px', fontWeight: 600 }}>
              ⚠️ {warning}
            </div>
          )}
        </div>
      </div>
      <button
        onClick={onButtonClick}
        style={{
          background: buttonColor,
          color: '#fff',
          border: 'none',
          borderRadius: '8px',
          padding: '12px 20px',
          fontSize: '15px',
          fontWeight: 600,
          cursor: 'pointer',
          width: '100%',
          transition: 'opacity 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '0.9';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
      >
        {buttonLabel}
      </button>
    </div>
  );
}

interface ModalProps {
  title: string;
  children: React.ReactNode;
  onClose?: () => void;
  isDismissible?: boolean;
}

function Modal({ title, children, onClose, isDismissible = true }: ModalProps) {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 5000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        overflowY: 'scroll',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '100px'
      }}
      onClick={isDismissible && onClose ? onClose : undefined}
    >
      <div style={{
        maxWidth: '500px',
        width: '100%',
        margin: '40px 20px 40px 20px',
        background: '#fff',
        borderRadius: '16px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        position: 'relative'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          padding: '24px 24px 16px 24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', margin: 0 }}>
            {title}
          </h2>
          {isDismissible && onClose && (
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '0',
                lineHeight: '1'
              }}
            >
              ×
            </button>
          )}
        </div>
        <div style={{ padding: '24px' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
